import React, {Fragment, useState, useRef, useImperativeHandle} from 'react';

import UserInput from "ui/ReactUserInput";
import UserInputCheckbox from "ui/ReactUserInputCheckbox";
import BootstrapRow from "ui/BootstrapRow";
import BootstrapCol from "ui/BootstrapCol";
import UserInputTextarea from "ui/ReactUserInputTextarea";
import DropdownAlternateProvisionReason from "ui/DropdownAlternateProvisionReason";
import FieldsetContainer from "root/Base/FieldsetContainer";
import {useChildContext} from "root/Contexts/ChildContext";

export const emptyFormData = {
   full_time_education_flag: 1,
   alternate_provision: null,
   alternate_provision_notes: null,
   ap_reason_id: null,
   alternate_provision_reason: null,
   alternate_provision_hours: null,
   alternate_provision_start_date: null,
   alternate_provision_agreement_date: null,
   school_pt_flag: 0,
   school_pt_reason: null,
   school_pt_hours: null,
   school_pt_start_date: null,
}

const FullTimeEducationForm = React.forwardRef(({readOnly, displayErrors, child, mode}, ref) =>
{
   const { isPost16 } = useChildContext() ;

   const initialIsInFullTimeEducationFlag = +(child.full_time_education_flag) === 1;
   const initialIsPartTimeTimetableFlag = +(child.ap_pt_flag) === 1;
   const initialIsPartTimeTimetableSchoolFlag = +(child.school_pt_flag) === 1;

   const initialApReason = child.ap_reason_id === null ? null : +(child.ap_reason_id);

   const [isInFullTimeEducationFlag, setIsInFullTimeEducationFlag] = useState(initialIsInFullTimeEducationFlag)
   const [isPartTimeTimetableFlag, setIsPartTimeTimetableFlag] = useState(initialIsPartTimeTimetableFlag)
   const [isPartTimeTimetableSchoolFlag, setIsPartTimeTimetableSchoolFlag] = useState(initialIsPartTimeTimetableSchoolFlag)
   const [apReason, setAPReason] = useState(initialApReason)

   const InputChildIsInFullTimeEducation = useRef(null);
   const InputPartTimeCheck = useRef(null);
   const InputAlternateProvisionStartDate = useRef(null);

   const InputAlternateProvisionAgreementDate = useRef(null);
   const InputAlternateProvision = useRef(null);
   const InputAlternateProvisionNotes = useRef(null);
   const InputTotalHours = useRef(null);
   const InputReason = useRef(null);

   const InputPartTimeStartDate = useRef(null);
   const InputPartTimeTotalHours = useRef(null);
   const InputPartTimeReason = useRef(null);

   const InputPartTimeSchoolStartDate = useRef(null);
   const InputPartTimeSchoolTotalHours = useRef(null);
   const InputPartTimeSchoolReason = useRef(null);

   useImperativeHandle(ref, () => ({
      getFormData()
      {
         return getFormDataLocal();
      },
      formIsValid()
      {
         return formIsValidLocal();
      },
   }))

   const getFormDataLocal = () =>
   {
      if (child.year_group_id > 1 || mode === 'create')
      {
         let base =
            {
               full_time_education_flag: isInFullTimeEducationFlag ? 1 : 0,
               alternate_provision: !isInFullTimeEducationFlag ? InputAlternateProvision.current.getValue() : null,
               alternate_provision_notes: !isInFullTimeEducationFlag ? InputAlternateProvisionNotes.current.getValue() : null,
               alternate_provision_reason: !isInFullTimeEducationFlag ? InputReason.current.getValue() : null,
               ap_reason_id: !isInFullTimeEducationFlag ? apReason : null,
               alternate_provision_hours: !isInFullTimeEducationFlag ? InputTotalHours.current.getValue() : null,
               alternate_provision_start_date: !isInFullTimeEducationFlag ? InputAlternateProvisionStartDate.current.getValue() : null,
               alternate_provision_agreement_date: !isInFullTimeEducationFlag ? InputAlternateProvisionAgreementDate.current.getValue() : null
            } ;

         let extraSchool = {} ;

         if ( isPartTimeTimetableSchoolFlag )
         {
            extraSchool = {
               school_pt_flag: isPartTimeTimetableSchoolFlag ? 1 : 0,
               school_pt_reason: !isInFullTimeEducationFlag ? InputPartTimeSchoolReason.current.getValue() : null,
               school_pt_hours: !isInFullTimeEducationFlag ? InputPartTimeSchoolTotalHours.current.getValue() : null,
               school_pt_start_date: !isInFullTimeEducationFlag ? InputPartTimeSchoolStartDate.current.getValue() : null,
            }
         }

         let extraPT = {} ;

         if ( isPartTimeTimetableFlag )
         {
            extraPT = {
               ap_pt_flag: isPartTimeTimetableFlag ? 1 : 0,
               ap_pt_reason: isPartTimeTimetableFlag ? InputPartTimeReason.current.getValue() : null,
               ap_pt_hours: isPartTimeTimetableFlag ? InputPartTimeTotalHours.current.getValue() : null,
               ap_pt_start_date: isPartTimeTimetableFlag ? InputPartTimeStartDate.current.getValue() : null,
            }
         }

         base = { ...base, ...extraSchool, ...extraPT } ;

         return base ;
      }
      else
      {
         return ( emptyFormData )
      }
   }

   const formIsValidLocal = () =>
   {
      let valid = true;

      if (!isInFullTimeEducationFlag)
      {
         if ( isPartTimeTimetableSchoolFlag )
         {
            if (!InputPartTimeSchoolStartDate.current.isValid())
            {
               valid = false;
            }

            if (!InputPartTimeSchoolTotalHours.current.isValid())
            {
               valid = false;
            }

            if (!InputPartTimeSchoolReason.current.isValid())
            {
               valid = false;
            }
         }

         if (!InputAlternateProvision.current.isValid())
         {
            valid = false;
         }

         if (!InputAlternateProvisionNotes.current.isValid())
         {
            valid = false;
         }

         if (!InputReason.current.isValid())
         {
            valid = false;
         }

         if (!InputTotalHours.current.isValid())
         {
            valid = false;
         }

         if (!InputAlternateProvisionStartDate.current.isValid())
         {
            valid = false;
         }

         if (!InputAlternateProvisionAgreementDate.current.isValid())
         {
            valid = false;
         }

         if (apReason === null)
         {
            valid = false;
         }
      }

      if ( isPartTimeTimetableFlag )
      {
         if (!InputPartTimeStartDate.current.isValid())
         {
            valid = false;
         }

         if (!InputPartTimeTotalHours.current.isValid())
         {
            valid = false;
         }

         if (!InputPartTimeReason.current.isValid())
         {
            valid = false;
         }
      }

      return valid;
   }

   const onChangeInFullTimeEducation = (newValue) =>
   {
      setIsInFullTimeEducationFlag(newValue);
   }

   const onChangePartTimeCheck = (newValue) =>
   {
      setIsPartTimeTimetableFlag(newValue);
   }

   const onChangePartTimeSchoolCheck = (newValue) =>
   {
      setIsPartTimeTimetableSchoolFlag(newValue);
   }
   const onReasonChange = (reason) =>
   {
      setAPReason(reason.value);
   }

   const isFullTimeEducationSection = () =>
   {
      return (
         <Fragment>
            <BootstrapRow key='brfe'>
               <BootstrapCol widel={12}>
                  <UserInputCheckbox
                     key='InputChildIsInFullTimeEducation'
                     ref={InputChildIsInFullTimeEducation}
                     label='Is the pupil attending full time school?'
                     defaultValue={initialIsInFullTimeEducationFlag}
                     readOnly={readOnly}
                     onChange={onChangeInFullTimeEducation}
                     displayErrors={displayErrors}/>
               </BootstrapCol>
            </BootstrapRow>
         </Fragment>
      )
   }

   const isPartTimeTimetableSection = () =>
   {
      return (
         <Fragment>
            <BootstrapRow key='brpt'>
               <BootstrapCol widel={12}>
                  <UserInputCheckbox
                     key='InputPartTimeCheck'
                     ref={InputPartTimeCheck}
                     label='Has a reduced timetable been agreed?'
                     defaultValue={initialIsPartTimeTimetableFlag}
                     readOnly={readOnly}
                     onChange={onChangePartTimeCheck}
                     displayErrors={displayErrors}/>
               </BootstrapCol>
            </BootstrapRow>
         </Fragment>
      )
   }

   const isPartTimeTimetableSchoolSection = () =>
   {
      return (
         <Fragment>
            <BootstrapRow key='brpt'>
               <BootstrapCol widel={12}>
                  <UserInputCheckbox
                     key='InputPartTimeCheck'
                     ref={InputPartTimeCheck}
                     label='Are they accessing a reduced timetable at school?'
                     defaultValue={initialIsPartTimeTimetableSchoolFlag}
                     readOnly={readOnly}
                     onChange={onChangePartTimeSchoolCheck}
                     displayErrors={displayErrors}/>
               </BootstrapCol>
            </BootstrapRow>
         </Fragment>
      )
   }

   const fromDateRow = () =>
   {
      const fromRequired = !isInFullTimeEducationFlag;

      return (
         <Fragment>
            <BootstrapRow key='agree'>
               <div key='altagree' className='col-md-3'>
                  <UserInput validation={{required: false, maxlen: 10, isDate: true}}
                             id='InputAlternateProvisionAgreementDate'
                             name='InputAlternateProvisionAgreementDate'
                             key='InputAlternateProvisionAgreementDate'
                             ref={InputAlternateProvisionAgreementDate}
                             placeholder='DD/MM/YYYY'
                             labelText='Alternate Provision Agreement Date'
                             errorText='You must enter a valid date'
                             value={child.dmy_alternate_provision_agreement_date}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>
               <div key='altstart' className='col-md-3'>
                  <UserInput validation={{required: fromRequired, maxlen: 10, isDate: true}}
                             id='InputAlternateProvisionStartDate'
                             name='InputAlternateProvisionStartDate'
                             key='InputAlternateProvisionStartDate'
                             ref={InputAlternateProvisionStartDate}
                             placeholder='DD/MM/YYYY'
                             labelText='Alternate Provision Start Date'
                             errorText='You must enter a valid date'
                             value={child.dmy_alternate_provision_start_date}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>
               <div key='altreasonid' className='col-md-4'>
                  <DropdownAlternateProvisionReason validation={{required: fromRequired}}
                                                    onChange={onReasonChange}
                                                    value={apReason === null ? null : +(apReason)}
                                                    readOnly={readOnly}
                                                    labelClassName='col-form-label'
                                                    label='Select the CME Reason' defaultClassName=''/>
                  {displayErrors === true && apReason === null &&
                     <div className='input-error-visible'>You must select a reason</div>}
               </div>

               <div key='althours' className='col-md-2'>
                  <UserInput validation={{required: fromRequired, maxlen: 50}}
                             id='InputTotalHours'
                             name='InputTotalHours'
                             key='InputTotalHours'
                             ref={InputTotalHours}
                             labelText='Number of Hours'
                             errorText='You must enter the number of hours attended'
                             value={child.alternate_provision_hours}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>
            </BootstrapRow>

            <BootstrapRow>
               <div key='altreason' className='col-md-12'>
                  <UserInputTextarea validation={{required: fromRequired, maxlen: 2000}}
                                     ref={InputReason} id='InputAlternateProvision'
                                     name='InputReason' key='InputReason'
                                     label='Details / Reason' errorText="You must enter details"
                                     displayErrors={displayErrors}
                                     value={child.alternate_provision_reason} rows={3} readOnly={readOnly}/>
               </div>
            </BootstrapRow>
         </Fragment>
      )
   }

   const partTimeTimetableRow = () =>
   {
      const fromRequired = isPartTimeTimetableFlag;

      return (
         <Fragment>
            <BootstrapRow key='parttime'>
               <div key='altstart' className='col-md-6'>
                  <UserInput validation={{required: fromRequired, maxlen: 10, isDate: true}}
                             id='InputPartTimeStartDate'
                             name='InputPartTimeStartDate'
                             key='InputPartTimeStartDate'
                             ref={InputPartTimeStartDate}
                             placeholder='DD/MM/YYYY'
                             labelText='Part Time Timetable Start Date'
                             errorText='You must enter a valid date'
                             value={child.dmy_ap_pt_start_date}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>

               <div key='althours' className='col-md-6'>
                  <UserInput validation={{required: fromRequired, maxlen: 50}}
                             id='InputPartTimeTotalHours'
                             name='InputPartTimeTotalHours'
                             key='InputPartTimeTotalHours'
                             ref={InputPartTimeTotalHours}
                             labelText='Number of Hours'
                             errorText='You must enter the number of hours attended'
                             value={child.ap_pt_hours}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>
            </BootstrapRow>

            <BootstrapRow>
               <div key='altreason' className='col-md-12'>
                  <UserInputTextarea validation={{required: fromRequired, maxlen: 2000}}
                                     ref={InputPartTimeReason} id='InputPartTimeReason'
                                     name='InputPartTimeReason' key='InputPartTimeReason'
                                     label='Details / Reason' errorText="You must enter details"
                                     displayErrors={displayErrors}
                                     value={child.ap_pt_reason} rows={3} readOnly={readOnly}/>
               </div>
            </BootstrapRow>
         </Fragment>
      )
   }
   const partTimeTimetableSchoolRow = () =>
   {
      const fromRequired = !isInFullTimeEducationFlag;

      return (
         <Fragment>
            <BootstrapRow key='parttime'>
               <div key='altstart' className='col-md-6'>
                  <UserInput validation={{required: fromRequired, maxlen: 10, isDate: true}}
                             id='InputPartTimeSchoolStartDate'
                             name='InputPartTimeSchoolStartDate'
                             key='InputPartTimeSchoolStartDate'
                             ref={InputPartTimeSchoolStartDate}
                             placeholder='DD/MM/YYYY'
                             labelText='School Reduced Timetable Start Date'
                             errorText='You must enter a valid date'
                             value={child.dmy_school_pt_start_date}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>

               <div key='althours' className='col-md-6'>
                  <UserInput validation={{required: fromRequired, maxlen: 50}}
                             id='InputPartTimeSchoolTotalHours'
                             name='InputPartTimeSchoolTotalHours'
                             key='InputPartTimeSchoolTotalHours'
                             ref={InputPartTimeSchoolTotalHours}
                             labelText='School Reduced Timetable Number of Hours'
                             errorText='You must enter the number of hours attended'
                             value={child.school_pt_hours}
                             readOnly={readOnly}
                             displayErrors={displayErrors}/>
               </div>
            </BootstrapRow>

            <BootstrapRow>
               <div key='altreason' className='col-md-12'>
                  <UserInputTextarea validation={{required: fromRequired, maxlen: 2000}}
                                     ref={InputPartTimeSchoolReason} id='InputPartTimeSchoolReason'
                                     name='InputPartTimeSchoolReason' key='InputPartTimeSchoolReason'
                                     label='Details / Reason' errorText="You must enter details"
                                     displayErrors={displayErrors}
                                     value={child.school_pt_reason} rows={3} readOnly={readOnly}/>
               </div>
            </BootstrapRow>
         </Fragment>
      )
   }

   const rowAlternateProvision = () =>
   {
      const required = !isInFullTimeEducationFlag;

      return (
         <Fragment>
            <BootstrapRow>
               <BootstrapCol key='InputNeedsDiv' width={6}>
                  <UserInputTextarea validation={{required: required, maxlen: 2000}}
                                     ref={InputAlternateProvision} id='InputAlternateProvision'
                                     name='InputAlternateProvision' key='InputAlternateProvision'
                                     label='Name of Alternate Provision'
                                     errorText="You must enter the alernative provider details"
                                     displayErrors={displayErrors}
                                     value={child.alternate_provision} rows={3} readOnly={readOnly}/>
               </BootstrapCol>
               <BootstrapCol key='InputProvisionDiv' width={6}>
                  <UserInputTextarea validation={{required: false, maxlen: 2000}}
                                     ref={InputAlternateProvisionNotes} id='InputAlternateProvisionNotes'
                                     name='InputAlternateProvisionNotes' key='InputAlternateProvisionNotes'
                                     label='Days, Times etc'
                                     errorText="You must enter details of the alternative provision"
                                     displayErrors={displayErrors}
                                     value={child.alternate_provision_notes} rows={3} readOnly={readOnly}/>
               </BootstrapCol>
            </BootstrapRow>
         </Fragment>
      )
   }

   const fullTimeEducationSection = () =>
   {
      return (
         <FieldsetContainer title='Full Time Education'>
            { isFullTimeEducationSection()}
            {!isInFullTimeEducationFlag === true && fromDateRow()}
            {!isInFullTimeEducationFlag === true && rowAlternateProvision()}
         </FieldsetContainer>
      )
   }

   const reducedTimetableSection = () =>
   {
      return (
         <FieldsetContainer title='Reduced Timetable'>
            { isPartTimeTimetableSection()}
            { isPartTimeTimetableFlag === true && partTimeTimetableRow() }
         </FieldsetContainer>
      )
   }

   return (
      <BootstrapRow>
         <BootstrapCol width={12}>
            { isPost16 === false && fullTimeEducationSection() }
            { isPost16 === false && reducedTimetableSection() }
         </BootstrapCol>
      </BootstrapRow>
   )
})

export default FullTimeEducationForm;
